/**
 * Tenant Selector Component
 * 
 * Dropdown for switching between organizations (tenants).
 * Appears in the dashboard header when user belongs to multiple tenants.
 * 
 * HIPAA COMPLIANCE NOTES:
 * - Tenant names are not PHI
 * - Switching tenants issues new JWT tokens
 */

import { useState, useRef, useEffect } from 'react';
import { useTenant } from '../../contexts';
import './TenantSelector.css';

export function TenantSelector() {
  const { tenants, currentTenant, hasMultipleTenants, switchTenant, isLoading } = useTenant();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't show if only one tenant
  if (!hasMultipleTenants || !currentTenant) {
    // Still show the current tenant name as a non-interactive element
    if (currentTenant) {
      return (
        <div className="tenant-selector tenant-selector--single">
          <span className="tenant-selector__icon">🏢</span>
          <span className="tenant-selector__name">{currentTenant.name}</span>
        </div>
      );
    }
    return null;
  }

  const handleTenantSwitch = async (tenantSlug: string) => {
    if (tenantSlug === currentTenant.slug) {
      setIsOpen(false);
      return;
    }

    try {
      await switchTenant(tenantSlug);
      // Page will reload after switch
    } catch (error) {
      console.error('Failed to switch tenant:', error);
    }
  };

  return (
    <div className="tenant-selector" ref={dropdownRef}>
      <button
        className={`tenant-selector__trigger ${isOpen ? 'tenant-selector__trigger--open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="tenant-selector__icon">🏢</span>
        <span className="tenant-selector__name">{currentTenant.name}</span>
        <span className="tenant-selector__arrow">
          {isOpen ? '▲' : '▼'}
        </span>
      </button>

      {isOpen && (
        <div className="tenant-selector__dropdown" role="listbox">
          <div className="tenant-selector__header">
            Switch Organization
          </div>
          <ul className="tenant-selector__list">
            {tenants.map((tenant) => (
              <li key={tenant.id}>
                <button
                  className={`tenant-selector__option ${
                    tenant.slug === currentTenant.slug ? 'tenant-selector__option--active' : ''
                  }`}
                  onClick={() => handleTenantSwitch(tenant.slug)}
                  role="option"
                  aria-selected={tenant.slug === currentTenant.slug}
                >
                  <span className="tenant-selector__option-icon">🏢</span>
                  <div className="tenant-selector__option-info">
                    <span className="tenant-selector__option-name">{tenant.name}</span>
                    <span className="tenant-selector__option-role">{tenant.role}</span>
                  </div>
                  {tenant.slug === currentTenant.slug && (
                    <span className="tenant-selector__option-check">✓</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {isLoading && (
        <div className="tenant-selector__loading">
          <span className="tenant-selector__spinner"></span>
          Switching...
        </div>
      )}
    </div>
  );
}

export default TenantSelector;
